"use client";

import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { Check, Copy } from "lucide-react";
import * as React from "react";

// Hata alert'lerinde backend'in `x-request-id` header'ını gösterir. Destek
// taleplerinde log eşleştirmesi için kullanıcının tek tıkla kopyalayabilmesi
// gerekiyor; clipboard API'si yoksa (http/eski tarayıcı) textarea fallback'i var.
function RequestIdCopy({ requestId }: { requestId: string }) {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(requestId);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = requestId;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
    } catch (error) {
      console.log("request id copy error", error);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={requestId}
      className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded border border-current bg-black/10 px-2 py-1 text-xs font-mono opacity-80 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-current"
    >
      <span className="shrink-0 opacity-70">ID:</span>
      <span className="truncate">{requestId}</span>
      {copied ? <Check className="h-3.5 w-3.5 shrink-0" /> : <Copy className="h-3.5 w-3.5 shrink-0" />}
    </button>
  );
}

export function Toaster() {
  const { toasts } = useToast();

  const variantName = {
    default: "Warnung",
    destructive: "Fehler",
    success: "Erfolg",
    warning: "Warnung",
    info: "Info",
    error: "Fehler",
  };
  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, message, requestId, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="grid min-w-0 gap-1">
              {<ToastTitle>{title ?? variantName[props.variant ?? "default"]}</ToastTitle>}
              {(description || message) && <ToastDescription>{description || message}</ToastDescription>}
              {requestId && <RequestIdCopy requestId={requestId} />}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
